import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import type { User } from "@supabase/supabase-js";

type MinimalUser = { id: string; email?: string | null };

export function useAuth() {
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [sessionReady, setSessionReady] = useState(false);
  const [sessionUser, setSessionUser] = useState<MinimalUser | null>(null);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      const user = data.session?.user as User | undefined;
      setSessionUser(user ? { id: user.id, email: user.email } : null);
      setSessionReady(true);
    });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      const user = session?.user as User | undefined;
      setSessionUser(user ? { id: user.id, email: user.email } : null);
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
    });
    return () => {
      data.subscription.unsubscribe();
    };
  }, [queryClient]);

  const { data: user } = useQuery({
    queryKey: ["/api/user"],
    enabled: sessionReady && !!sessionUser,
    retry: false,
    meta: {
      showErrorNotification: false,
    },
    throwOnError: false,
  });

  const clearClientAuthState = () => {
    setSessionUser(null);
    queryClient.setQueryData(["/api/user"], null);
    queryClient.clear();
  };

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: async () => {
      // Local scope guarantees this device signs out even if network/API calls fail.
      const { error } = await supabase.auth.signOut({ scope: "local" });
      if (error) throw error;
    },
    onSuccess: () => {
      clearClientAuthState();
      toast({
        title: "Logged out",
        description: "You have been successfully logged out.",
      });
      setLocation("/");
    },
    onError: (error: Error) => {
      console.error("Logout error:", error);
      // Best-effort local cleanup to avoid sticky logged-in UI state.
      clearClientAuthState();
      setLocation("/");
    },
  });

  return {
    user: (user as any) || sessionUser,
    isLoading: !sessionReady,
    isAuthenticated: !!sessionUser,
    isLoggingOut: logoutMutation.isPending,
    logout: () => logoutMutation.mutate(),
  };
}
